// Лента точки в кэше запросов (T-047): опрос вперёд по курсору `since`.

import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useEffect, useState, useSyncExternalStore } from 'react';

import { HttpError } from '@/shared/api';
import { useLocations } from '@/modules/locations';
import {
  loadKioskBinding,
  subscribeKioskBinding,
  useSupportSession,
  type KioskBinding,
} from '@/shared/auth';
import { useSession } from '@/shared/session';

import { deviceMarksVersion, seenMark, subscribeDeviceMarks } from '../model/deviceMarks';
import { mergeFeed, unreadCount } from '../model/mergeFeed';
import { alertFeedApi, type FeedMessage } from './alertFeedApi';
import { alertKeys } from './queryKeys';

/** Раз в минуту: пороги считает крон раз в 5 минут, мгновенная доставка ничего не даёт. */
export const FEED_POLL_MS = 60_000;

export type FeedState =
  /** Сервер старше T-047 (404): функция выключена, без ошибки на экране. */
  | { readonly available: false }
  | {
      readonly available: true;
      /** Новые первыми, не больше 100. */
      readonly messages: readonly FeedMessage[];
      readonly serverTime: string;
    };

async function fetchLatest(client: QueryClient, location: string): Promise<FeedState> {
  const known = client.getQueryData<FeedState>(alertKeys.latest(location));
  const messages = known?.available ? known.messages : [];
  const since = messages[0]?.created_at;
  try {
    const page = await alertFeedApi.page(location, since ? { since } : {});
    return {
      available: true,
      messages: mergeFeed(messages, page.results, page.has_more),
      serverTime: page.server_time,
    };
  } catch (error) {
    if (error instanceof HttpError && error.status === 404) return { available: false };
    throw error;
  }
}

/**
 * Лента точки. Интервал (`poll`) ставит ТОЛЬКО наблюдатель в `AppLayout`: колокольчики
 * и экран ленты читают тот же ключ без своего таймера — лишних запросов нет.
 *
 * Опрос идёт и в скрытом окне (свёрнуто, в трее): иначе уведомление Windows не пришло бы
 * ровно тогда, когда оно нужно. `backgroundThrottling` оболочки не трогаем — на видимости
 * страницы держится экран принтера; Chromium будит таймеры скрытого окна раз в минуту,
 * при кроне раз в 5 минут это терпимо.
 */
export function useAlertFeed(
  location: string | null,
  options: { readonly enabled?: boolean; readonly poll?: boolean } = {},
) {
  const client = useQueryClient();
  const poll = options.poll ?? false;
  return useQuery({
    queryKey: alertKeys.latest(location ?? ''),
    queryFn: () => fetchLatest(client, location as string),
    enabled: (options.enabled ?? true) && location !== null,
    staleTime: 30_000,
    refetchInterval: poll
      ? (query) => (query.state.data?.available === false ? false : FEED_POLL_MS)
      : false,
    refetchIntervalInBackground: poll,
  });
}

/**
 * Точка ленты этой сессии. На закреплённом планшете — ТОЧКА ПЛАНШЕТА (№10, 2026-09-30):
 * раньше бралась основная точка вошедшего, и владелец с другой основной точкой (или без
 * неё) на планшете Покровки ленту Покровки не видел — уведомления Windows молчали.
 * Точка планшета берётся, только если она есть среди точек человека; иначе — его
 * основная. В саппорт-сессии ленты нет.
 */
export function useFeedLocation(): string | null {
  const user = useSession().data;
  const support = useSupportSession() !== null;
  const binding = useKioskBinding();
  const locations = useLocations().data?.results;
  if (support || !user || user.role === 'platform_admin') return null;
  if (binding) {
    const bound = locations?.find((location) =>
      binding.locationId
        ? location.id === binding.locationId
        : location.name === binding.locationName,
    );
    if (bound) return bound.id;
  }
  return user.default_location ?? null;
}

/** Закрепление этого устройства за точкой; `null` — не закреплено (или ещё не прочитано). */
function useKioskBinding(): KioskBinding | null {
  const [binding, setBinding] = useState<KioskBinding | null>(null);
  useEffect(() => {
    let alive = true;
    const read = (): void => {
      void loadKioskBinding().then((next) => {
        if (alive) setBinding(next);
      });
    };
    read();
    const unsubscribe = subscribeKioskBinding(read);
    return () => {
      alive = false;
      unsubscribe();
    };
  }, []);
  return binding;
}

/** Сервер отдаёт ленту основной точки: колокольчик ведёт на экран ленты. */
export function useAlertFeedAvailable(): boolean {
  const location = useFeedLocation();
  return useAlertFeed(location).data?.available === true;
}

/** Непрочитанные на этом устройстве по основной точке. */
export function useUnreadAlerts(): number {
  const location = useFeedLocation();
  const data = useAlertFeed(location).data;
  useSyncExternalStore(subscribeDeviceMarks, deviceMarksVersion, deviceMarksVersion);
  if (location === null || !data?.available) return 0;
  return unreadCount(data.messages, seenMark(location));
}
